<template>
  <div>
    <div class="vx-row">
      <div class="vx-col">
        <validation-provider
          ref="start-date-validator"
          v-slot="{ errors }"
          :rules="{
            required: true,
            date_format: 'YYYY-MM-DD',
            after_date: {
              minDate: dates.start,
              included: true,
              format: 'YYYY-MM-DD',
            },
            before_date: {
              maxDate: endDate,
              included: false,
              format: 'YYYY-MM-DD',
            },
          }"
          class="mt-2 w-full"
          vid="startDate"
        >
          <vs-input
            v-model="startDate"
            label="From (YYYY-MM-DD)"
            :disabled="disabled"
            @input="validateAndEmitDates"
          />
          <span class="text-danger text-sm">{{ errors[0] }}</span>
        </validation-provider>
      </div>
      <div class="vx-col">
        <validation-provider
          ref="end-date-validator"
          v-slot="{ errors }"
          :rules="{
            required: true,
            date_format: 'YYYY-MM-DD',
            after_date: {
              minDate: startDate,
              included: false,
              format: 'YYYY-MM-DD',
            },
            before_date: {
              maxDate: dates.end,
              included: true,
              format: 'YYYY-MM-DD',
            },
          }"
          class="mt-2 w-full"
          vid="endDate"
        >
          <vs-input
            v-model="endDate"
            label="To (YYYY-MM-DD)"
            :disabled="disabled"
            @input="validateAndEmitDates"
          />
          <span class="text-danger text-sm">{{ errors[0] }}</span>
        </validation-provider>
      </div>
    </div>
    <div v-if="filteredEvents && filteredEvents.length > 0" class="vx-row mt-4 pl-2">
      <div
        v-for="(event, index) in filteredEvents"
        :key="index"
        class="mx-2"
        @click="selectEvent(index)"
      >
        <vs-chip
          v-tooltip="{
            content: getEventTooltipContent(index),
            boundariesElement: 'window',
          }"
          color="primary"
          :transparent="selectedEventIndex != index"
          class="text-sm md:text-base"
          :class="[
            { 'event-chip': !disabled },
            { 'event-chip-disabled': disabled },
            { 'text-primary': selectedEventIndex != index },
          ]"
          @mouseenter="logTooltip"
        >
          <vs-avatar icon="event" />
          {{ event.name }}
        </vs-chip>
      </div>
    </div>
    <div class="vx-row mt-4 px-4">
      <vs-button
        color="primary"
        type="border"
        :disabled="(startDate == dates['start'] && endDate == dates['end']) || disabled"
        @click="resetButton"
        >Full period</vs-button
      >
    </div>
  </div>
</template>

<script>
import moment from "moment";

import { TOOLTIP_DISPLAYED_EVENT } from "@/assets/constants/analytics";

export default {
  name: "DateRangePicker",
  props: {
    dates: {
      type: Object,
      required: true,
    },
    /*
    Array of objects containing the following properties:
    - name, name of the shortcut (will be displayed)
    - start, the start date of the shortcut
    - end, the end date of the shortcut
    */
    events: {
      type: Array,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      startDate: this.dates["start"],
      endDate: this.dates["end"],
      selectedEventIndex: -1,
    };
  },
  computed: {
    filteredEvents() {
      return this.events.filter(
        (event) => event.start >= this.dates.start && (event.end <= this.dates.end || !event.end)
      );
    },
  },
  watch: {
    dates() {
      this.startDate = this.dates["start"];
      this.endDate = this.dates["end"];
      this.selectedEventIndex = -1;
    },
  },
  methods: {
    async validateDates() {
      // Unfortunately, we need to do a manual verification here, because
      // the @input event fire before vee-validate can update the value
      // for the validation, hence we will validate with input change
      // lag, giving wrong or correct validation at wrong time.
      const validations = await Promise.all([
        this.$refs["start-date-validator"].validate(this.startDate),
        this.$refs["end-date-validator"].validate(this.endDate),
      ]);
      return validations.every((validation) => validation.valid);
    },
    async validateAndEmitDates() {
      const validDates = await this.validateDates();
      if (validDates) {
        this.selectedEventIndex = -1;
        this.emitDates({ method: "manual" });
      }
    },
    async emitDates(updateInfo) {
      this.$emit(
        "dates-change",
        {
          start: this.startDate,
          end: this.endDate,
        },
        { ...updateInfo, loc: "DateRangePicker" }
      );
    },
    // Set the dates to the analysis period if the period was changed with zoom
    setDates(dates, emit = false) {
      this.selectedEventIndex = -1;
      this.startDate = dates.start;
      this.endDate = dates.end;

      // Only emit the dates-change event if asked.
      if (emit) {
        this.emitDates();
      }
    },
    // Function called when clicking on the reset button
    resetButton(emit = true) {
      this.selectedEventIndex = -1;
      this.startDate = this.dates["start"];
      this.endDate = this.dates["end"];

      // Only emit the dates-change event if asked.
      if (emit) {
        this.emitDates();
      }
    },
    selectEvent(index, emit = true) {
      if (this.disabled) return;
      // If we re-click on the same event, remove it and reset the date range,
      // otherwise update the date range with the dates of the event.
      if (index == this.selectedEventIndex) {
        this.selectedEventIndex = -1;
        this.startDate = this.dates["start"];
        this.endDate = this.dates["end"];
      } else {
        this.selectedEventIndex = index;
        const selectedEvent = this.filteredEvents[this.selectedEventIndex];
        // If start date or end date is null, use the earliest or latest available one.
        const startDate = selectedEvent["start"] || this.dates["start"];
        const endDate = selectedEvent["end"] || this.dates["end"];
        this.startDate = startDate;
        this.endDate = endDate;
      }

      // Only emit the dates-change event if asked.
      if (emit) {
        this.emitDates({
          method: "event-click",
          periodName: this.filteredEvents[index].name,
        });
      }
    },
    getEventTooltipContent(index) {
      const event = this.filteredEvents[index];

      // Get start and end date to display; if null value is found, use the
      // start or end date of the entire date range that we have.
      let startDateStr;
      if (event["start"]) {
        startDateStr = moment(event["start"]).format("DD MMM YYYY");
      } else {
        startDateStr = moment(this.dates["start"]).format("DD MMM YYYY");
      }
      let endDateStr;
      if (event["end"]) {
        endDateStr = moment(event["end"]).format("DD MMM YYYY");
      } else {
        endDateStr = moment(this.dates["end"]).format("DD MMM YYYY");
      }

      return `${startDateStr} - ${endDateStr}`;
    },
    logTooltip() {
      this.$analytics.logEvent(TOOLTIP_DISPLAYED_EVENT, {
        tooltip: "date_range_picker_event",
      });
    },
  },
};
</script>
<style lang="scss">
@import "@/assets/scss/vuexy/variables/_main.scss";

.date-time-picker,
.date-time-picker input,
.date-time-picker label,
.date-time-picker p,
.date-time-picker span {
  font-family: $font-family-sans-serif;
}
.event-chip {
  font-weight: 500;
  cursor: pointer;
}
.event-chip-disabled {
  font-weight: 500;
  cursor: not-allowed;
}
</style>
